/* LoginPage — authentication entry point */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { Turnstile } from '@marsidev/react-turnstile';
import { useUser } from '../context/UserContext';
import { csrfFetch } from '../utils/csrfFetch';

/**
 * Phase 2 Turnstile site key — build-time public env. When unset, the
 * widget doesn't render and the submit isn't gated on a token; the backend
 * has a matching feature flag (TURNSTILE_SECRET_KEY) so both ends are
 * dormant together. Once the operator configures Cloudflare and sets both
 * env vars, the widget appears and verification is enforced.
 */
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || '';
const TURNSTILE_ENABLED = Boolean(TURNSTILE_SITE_KEY);

/**
 * What ?sso_error=<code> means, when the server sends someone back here from
 * the Cognito user pool's callback. The precise reason is in its auth_event.
 */
const SSO_ERRORS = {
  unavailable: 'Passkey and authenticator sign-in is unavailable right now. Try again shortly, or sign in with your password.',
  failed: "That sign-in didn't complete. Please try again.",
  no_account: 'There is no Decisiv Performance account for that sign-in. Ask your administrator for an invitation.',
  EmailNotVerified: 'Please verify your email before signing in.',
  PendingApproval: 'Your account is awaiting admin review.',
  AccountSuspended: 'Your account has been suspended. Contact support.',
  AccountRejected: 'Your account was not approved.',
};

/**
 * LoginPage - Authentication gate (DOS-30)
 *
 * Modes: login | register | forgot-password | reset-password
 *        | check-email (post-register) | verify-email (token landing)
 */
export default function LoginPage() {
  const { login, register, loading, checkAuth } = useUser();
  const navigate = useNavigate();
  // Auto-enter verify-email mode if URL is /verify-email?token=...
  const initialMode = (() => {
    if (typeof window === 'undefined') return 'login';
    const path = window.location.pathname;
    if (path === '/verify-email') return 'verify-email';
    return 'login';
  })();
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  // Phase 3: KYC fields collected on Register (DB-enforced non-empty)
  const [company, setCompany] = useState('');
  const [businessPurpose, setBusinessPurpose] = useState('');
  const [tempPassword, setTempPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [registrationEnabled, setRegistrationEnabled] = useState(true);
  const [ssoEnabled, setSsoEnabled] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [submittingReset, setSubmittingReset] = useState(false);
  const [submittingForgotUsername, setSubmittingForgotUsername] = useState(false);
  const [resendingVerification, setResendingVerification] = useState(false);
  // TODO #44: lazy-mount the Turnstile widget on the Check-Your-Email page.
  // false = button visible, widget hidden (first click arms it); true = widget
  // mounted, button enables only after the user passes the challenge. Avoids
  // showing two "Success!" widgets unprompted in the same signup flow.
  const [resendArmed, setResendArmed] = useState(false);
  // TODO #42 (2026-06-05): OWASP two-step verify. State machine:
  //   'pending'           — initial GET to validator in flight
  //   'ready-to-confirm'  — validator says token is usable; awaiting user click
  //   'confirming'        — POST to /confirm in flight after button click
  //   'success'           — POST consumed token + signed user in (trusted domain)
  //   'pending_approval'  — POST consumed token; account is awaiting admin (non-trusted)
  //   'failed'            — validator or confirm rejected (invalid/expired/server-error)
  const [verifyState, setVerifyState] = useState('pending');
  // Stash the plain token from the URL after validator success so the
  // button-click POST can replay it. Cleared on any failure path.
  const [verifyToken, setVerifyToken] = useState(null);
  // Phase 2: Turnstile token captured from the widget callback. Cleared
  // after each submit so a single token is single-use (matches Cloudflare's
  // own token semantics).
  const [turnstileToken, setTurnstileToken] = useState('');
  // Honeypot field — bots fill this, humans never see it. Kept in state
  // only so the value flows through controlled-input semantics; we never
  // call setHoneypot from a real UX flow.
  const [honeypot, setHoneypot] = useState('');
  const turnstileRef = useRef(null);

  useEffect(() => {
    csrfFetch('/api/auth/config').then(r => r.json()).then(cfg => {
      if (!cfg.registrationEnabled) setRegistrationEnabled(false);
      if (cfg.ssoEnabled) setSsoEnabled(true);
    }).catch(() => {});

    // Back from the Cognito user pool without signing in (?sso_error=...)
    const params = new URLSearchParams(window.location.search);
    const ssoError = params.get('sso_error');
    if (ssoError) {
      setError(SSO_ERRORS[ssoError] || SSO_ERRORS.failed);
      params.delete('sso_error');
      const rest = params.toString();
      window.history.replaceState(null, '', window.location.pathname + (rest ? `?${rest}` : ''));
    }

    // Check if we were redirected here due to session expiry
    if (sessionStorage.getItem('sessionExpired')) {
      setSessionExpired(true);
      sessionStorage.removeItem('sessionExpired');
    }
  }, []);

  // TODO #42 (2026-06-05): OWASP two-step verify-email flow.
  // On mount: extract ?token=... from URL and hit the read-only validator
  // (GET /api/auth/verify-email). The validator is idempotent — a URL
  // pre-scanner (Microsoft Safelinks, Mimecast, Proofpoint, etc.) can
  // follow the email link any number of times without consuming the token.
  // On validator success we transition to 'ready-to-confirm' and show an
  // "Activate my account" button; only the button-click POST to
  // /api/auth/verify-email/confirm actually mutates state.
  //
  // Dependency: only `mode` — NOT `checkAuth`. The pre-#42 code re-fired
  // this effect when `checkAuth`'s identity changed, which caused the
  // double-consume that surfaced as TODO #42 Defect B. With the validator
  // being idempotent, even a stray re-fire is now safe — but trimming the
  // dep also avoids needless network noise.
  useEffect(() => {
    if (mode !== 'verify-email') return;
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    if (!token) {
      setVerifyState('failed');
      return;
    }
    setVerifyState('pending');
    (async () => {
      try {
        const res = await csrfFetch(`/api/auth/verify-email?token=${encodeURIComponent(token)}`, {
          headers: { Accept: 'application/json' },
          credentials: 'include',
        });
        if (!res.ok) {
          setVerifyToken(null);
          setVerifyState('failed');
          return;
        }
        const data = await res.json();
        if (data?.valid === true) {
          setVerifyToken(token);
          setVerifyState('ready-to-confirm');
        } else {
          setVerifyToken(null);
          setVerifyState('failed');
        }
      } catch {
        setVerifyToken(null);
        setVerifyState('failed');
      }
    })();
  }, [mode]);

  /**
   * Phase 2 bot-check payload — included in every signup-adjacent request
   * body. When Turnstile isn't configured, the token is empty and the
   * backend's feature-flag bypass lets the request through. The honeypot
   * field is always sent (always empty for humans).
   */
  function botCheckFields() {
    return {
      'cf-turnstile-response': turnstileToken,
      company_url: honeypot,
    };
  }

  /** Reset the Turnstile widget after a submit so the user can retry. */
  function resetTurnstile() {
    setTurnstileToken('');
    setResendArmed(false);
    if (turnstileRef.current?.reset) {
      try { turnstileRef.current.reset(); } catch { /* ignore */ }
    }
  }

  /**
   * TODO #42: Activate-my-account click handler. Fires the consuming POST
   * to /api/auth/verify-email/confirm. Until this runs, the token has not
   * been consumed — only a deliberate user action triggers the mutation.
   */
  async function handleActivateClick() {
    if (!verifyToken) {
      setVerifyState('failed');
      return;
    }
    setVerifyState('confirming');
    try {
      const res = await csrfFetch('/api/auth/verify-email/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ token: verifyToken }),
      });
      if (!res.ok) {
        setVerifyToken(null);
        setVerifyState('failed');
        return;
      }
      const data = await res.json();
      if (data?.verified && data?.status === 'pending_approval') {
        // Phase 3: email verified but the domain isn't on the trusted
        // allowlist — account waits for admin review. NO session was
        // created server-side, so we just show the holding screen.
        setVerifyToken(null);
        setVerifyState('pending_approval');
      } else if (data?.verified) {
        setVerifyToken(null);
        setVerifyState('success');
        // Hydrate session state in UserContext, then redirect home.
        await checkAuth?.();
        setTimeout(() => { window.location.replace('/'); }, 800);
      } else {
        setVerifyToken(null);
        setVerifyState('failed');
      }
    } catch {
      setVerifyToken(null);
      setVerifyState('failed');
    }
  }

  async function handleResendVerification() {
    if (!email) {
      setError('Please enter your email address');
      return;
    }
    setError(null);
    setResendingVerification(true);
    try {
      const res = await csrfFetch('/api/auth/resend-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, ...botCheckFields() }),
      });
      if (res.status === 429) {
        setError('Too many requests. Try again later.');
      } else {
        setSuccess('If an unverified account with that email exists, a new verification link has been sent.');
      }
    } catch {
      setError('Could not request a new verification email.');
    } finally {
      setResendingVerification(false);
      resetTurnstile();
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    try {
      if (mode === 'register') {
        if (!company.trim() || !businessPurpose.trim()) {
          setError('Company and business purpose are required.');
          return;
        }
        const result = await register(
          email, password, name || null,
          { company: company.trim(), businessPurpose: businessPurpose.trim() },
          { turnstileToken, honeypot }
        );
        // New flow: registration returns { verified: false } — never auto-login.
        if (result && result.verified === false) {
          setPassword('');
          setMode('check-email');
          setSuccess(null);
          resetTurnstile();
          return;
        }
      } else {
        await login(email, password);
        // Land on / so the URL matches a real Route. Without this,
        // signing in from /login (bookmark, marketing link, direct
        // navigation) leaves URL=/login, AuthGate flips to children,
        // and the SPA renders NotFoundPage via the catchall.
        navigate('/', { replace: true });
      }
    } catch (err) {
      // Check if backend returned TempPasswordLogin (forgot-password flow)
      if (err.message === 'TempPasswordLogin' || err.code === 'TempPasswordLogin') {
        setMode('reset-password');
        setTempPassword(password); // they entered the temp password
        setPassword('');
        setError(null);
        setSuccess('You logged in with a temporary password. Please set a new password below.');
        return;
      }
      // Email-not-verified: switch into the check-email view so user can resend.
      if (err.code === 'EmailNotVerified' || err.message === 'EmailNotVerified') {
        if (err.email) setEmail(err.email);
        setPassword('');
        setMode('check-email');
        setError(null);
        return;
      }
      // Phase 3 signup_status non-active states — render dedicated messages
      // instead of the generic "Login failed" error toast.
      if (err.code === 'PendingApproval') {
        setPassword('');
        setError('Your account is awaiting admin review. You will receive an email when approved.');
        return;
      }
      if (err.code === 'AccountSuspended') {
        setPassword('');
        setError('Your account has been suspended. Please contact support.');
        return;
      }
      if (err.code === 'AccountRejected') {
        setPassword('');
        setError('Your account was not approved.');
        return;
      }
      if (mode === 'login') {
        setFailedAttempts(prev => prev + 1);
      }
      setError(err.message);
    }
  };

  /**
   * Phase 3 — POST /api/auth/forgot-username. Mirrors handleForgotPassword
   * but for the username reminder. OWASP-canonical uniform response, so we
   * always show the same success message regardless of whether the email
   * matched a real account.
   */
  const handleForgotUsername = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    if (!email) {
      setError('Please enter your email address first');
      return;
    }
    setSubmittingForgotUsername(true);
    try {
      const res = await csrfFetch('/api/auth/forgot-username', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, ...botCheckFields() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send username reminder');
      setSuccess(data.message || 'If an account exists for that email, a username reminder has been sent.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmittingForgotUsername(false);
      resetTurnstile();
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    if (!email) {
      setError('Please enter your email address first');
      return;
    }
    setSubmittingReset(true);
    try {
      const res = await csrfFetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, ...botCheckFields() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send reset email');
      setSuccess(data.message || 'If an account with that email exists, a temporary password has been sent.');
      setMode('reset-password');
      setTempPassword('');
      setNewPassword('');
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmittingReset(false);
      resetTurnstile();
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    if (!tempPassword || !newPassword) {
      setError('Both temporary password and new password are required');
      return;
    }
    if (newPassword.length < 6) {
      setError('New password must be at least 6 characters');
      return;
    }
    setSubmittingReset(true);
    try {
      const res = await csrfFetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, tempPassword, newPassword }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to reset password');
      setSuccess('Password has been reset! Signing you in...');
      // Auto-login inline — AuthGate unmounts this component when it succeeds.
      // No setTimeout / no field clears: both caused a visible flash of an
      // empty Reset Your Password form before the auth state propagated.
      try {
        await login(email, newPassword);
      } catch (err) {
        setMode('login');
        setError(err.message);
        setSuccess(null);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmittingReset(false);
    }
  };

  // Phase 2 gates — when Turnstile is configured, submit waits on a token.
  // When it isn't (dev / pre-rollout), the gate is open.
  const turnstileBlockingSubmit = TURNSTILE_ENABLED && !turnstileToken;

  // Render-only helpers reused across three forms.
  const turnstileWidget = TURNSTILE_ENABLED ? (
    <div className="my-3 flex justify-center">
      <Turnstile
        ref={turnstileRef}
        siteKey={TURNSTILE_SITE_KEY}
        onSuccess={setTurnstileToken}
        onError={() => setTurnstileToken('')}
        onExpire={() => setTurnstileToken('')}
        options={{ theme: 'auto', size: 'normal' }}
      />
    </div>
  ) : null;

  // Honeypot — visually hidden but submitted on every signup-adjacent form.
  // Naive bots fill every named input; legitimate users (browsing with CSS)
  // never see this field. Backend rejects requests with company_url set.
  const honeypotInput = (
    <input
      type="text"
      name="company_url"
      value={honeypot}
      onChange={e => setHoneypot(e.target.value)}
      tabIndex={-1}
      autoComplete="off"
      aria-hidden="true"
      style={{
        position: 'absolute',
        width: '1px',
        height: '1px',
        padding: 0,
        margin: '-1px',
        overflow: 'hidden',
        clip: 'rect(0,0,0,0)',
        whiteSpace: 'nowrap',
        border: 0,
      }}
    />
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <div className="w-14 h-14 bg-linear-to-br from-decisiv-blue to-amber-600 rounded-2xl
            flex items-center justify-center shadow-lg">
            <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          </div>
        </div>

        <h1 className="text-center text-2xl font-bold text-gray-900 dark:text-white mb-2">
          {mode === 'forgot-password' ? 'Forgot Password'
            : mode === 'reset-password' ? 'Reset Your Password'
            : mode === 'register' ? 'Create your account'
            : mode === 'check-email' ? 'Check your email'
            : mode === 'verify-email' && verifyState === 'ready-to-confirm' ? 'Almost there'
            : mode === 'verify-email' && verifyState === 'confirming' ? 'Activating your account…'
            : mode === 'verify-email' ? 'Verifying your email…'
            : 'Sign in to DP'}
        </h1>
        <p className="text-center text-sm text-gray-500 dark:text-gray-400 mb-8">
          {mode === 'forgot-password' ? 'Enter your email to receive a temporary password'
            : mode === 'reset-password' ? 'Enter your temporary password and choose a new one'
            : mode === 'register' ? 'Join your organization on DP'
            : mode === 'check-email' ? 'We sent you a verification link'
            : mode === 'verify-email' && verifyState === 'ready-to-confirm' ? 'One click to finish activating your account'
            : mode === 'verify-email' && verifyState === 'confirming' ? 'Setting up your session'
            : mode === 'verify-email' ? 'Validating your verification link'
            : 'Decisiv Performance'}
        </p>

        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xs border border-gray-200
          dark:border-gray-700 p-6">

          {sessionExpired && (
            <div className="mb-4 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/20
              text-amber-700 dark:text-amber-400 text-sm">
              Your session has expired. Please sign in again.
            </div>
          )}

          {error && (
            <div className="mb-4 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20
              text-red-700 dark:text-red-400 text-sm">
              {error}
            </div>
          )}

          {success && (
            <div className="mb-4 px-3 py-2 rounded-lg bg-green-50 dark:bg-green-900/20
              text-green-700 dark:text-green-400 text-sm">
              {success}
            </div>
          )}

          {/* Verify Email (landing page from email link) */}
          {mode === 'verify-email' ? (
            <div className="text-center py-4">
              {verifyState === 'pending' && (
                <>
                  <div className="w-10 h-10 mx-auto mb-4 border-4 border-decisiv-blue/30 border-t-decisiv-blue rounded-full animate-spin" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">Verifying your email…</p>
                </>
              )}
              {verifyState === 'ready-to-confirm' && (
                <>
                  <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-decisiv-blue/10 dark:bg-decisiv-blue/20 flex items-center justify-center">
                    <svg className="w-6 h-6 text-decisiv-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <p className="text-sm text-gray-700 dark:text-gray-300 mb-1">
                    Your verification link is good.
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-6">
                    Click below to activate your account and sign in.
                  </p>
                  <button
                    onClick={handleActivateClick}
                    className="px-6 py-2 bg-decisiv-blue text-white rounded-lg
                               hover:bg-decisiv-blue/90 transition font-medium text-sm"
                  >
                    Activate my account
                  </button>
                </>
              )}
              {verifyState === 'confirming' && (
                <>
                  <div className="w-10 h-10 mx-auto mb-4 border-4 border-decisiv-blue/30 border-t-decisiv-blue rounded-full animate-spin" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">Activating your account…</p>
                </>
              )}
              {verifyState === 'success' && (
                <>
                  <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-green-50 dark:bg-green-900/20 flex items-center justify-center">
                    <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-sm text-gray-700 dark:text-gray-300 mb-1">Email verified.</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Signing you in…</p>
                </>
              )}
              {verifyState === 'pending_approval' && (
                <>
                  <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center">
                    <svg className="w-6 h-6 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M12 8v4m0 4h.01M12 2a10 10 0 100 20 10 10 0 000-20z" />
                    </svg>
                  </div>
                  <p className="text-sm text-gray-700 dark:text-gray-300 mb-1">
                    Email verified.
                  </p>
                  <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
                    Your account is awaiting admin review.
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-6">
                    We&rsquo;ll send you an email when your account is approved.
                  </p>
                  <button
                    onClick={() => {
                      // Use React Router's navigate() so RR's internal location
                      // state syncs with the URL bar. Pre-fix this called
                      // window.history.replaceState which only updates the URL
                      // bar — RR's useLocation() still returned '/verify-email',
                      // App.jsx's /verify-email Route stayed matched, and the
                      // LoginPage never unmounted after a successful login.
                      // Symptom: "Sign in" button clicks succeeded server-side
                      // but the page never navigated to home. (2026-06-09 RCA
                      // — sibling to TODO #51's URL-hangover fix.)
                      if (window.location.pathname === '/verify-email') {
                        navigate('/', { replace: true });
                      }
                      setMode('login');
                      setError(null);
                      setSuccess(null);
                    }}
                    className="text-sm text-decisiv-blue hover:underline font-medium"
                  >
                    Back to sign in
                  </button>
                </>
              )}
              {verifyState === 'failed' && (
                <>
                  <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center">
                    <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
                    This verification link is invalid or has expired.
                  </p>
                  <button
                    onClick={() => {
                      if (window.location.pathname === '/verify-email') {
                        navigate('/', { replace: true });
                      }
                      setMode('login');
                      setError(null);
                      setSuccess(null);
                    }}
                    className="text-sm text-decisiv-blue hover:underline font-medium"
                  >
                    Back to sign in
                  </button>
                </>
              )}
            </div>

          ) : mode === 'check-email' ? (
            /* After register or login-blocked-by-EmailNotVerified */
            <div className="text-center py-2">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-decisiv-blue/10 flex items-center justify-center">
                <svg className="w-6 h-6 text-decisiv-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-1">
                We sent a verification link to:
              </p>
              <p className="text-sm font-semibold text-gray-900 dark:text-white mb-4 break-all">
                {email || 'your email'}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-6">
                Click the link in that email to activate your account. The link expires in 24 hours.
              </p>
              {honeypotInput}
              {resendArmed && turnstileWidget}
              <button
                onClick={() => {
                  if (TURNSTILE_ENABLED && !resendArmed) {
                    setResendArmed(true);
                    return;
                  }
                  handleResendVerification();
                }}
                disabled={resendingVerification || (resendArmed && turnstileBlockingSubmit)}
                className="w-full py-2.5 text-sm font-semibold bg-decisiv-blue text-white rounded-lg
                  hover:bg-decisiv-blue/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed mb-3"
              >
                {resendingVerification ? 'Sending…' : 'Resend verification email'}
              </button>
              <button
                onClick={() => { setMode('login'); setError(null); setSuccess(null); }}
                className="text-sm text-decisiv-blue hover:underline font-medium"
              >
                Back to sign in
              </button>
            </div>

          ) : mode === 'forgot-password' ? (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@dealership.com"
                  required
                  autoComplete="email"
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600
                    rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                    placeholder-gray-400 dark:placeholder-gray-500
                    focus:outline-hidden focus:ring-2 focus:ring-decisiv-blue focus:border-transparent"
                />
              </div>
              {honeypotInput}
              {turnstileWidget}
              <button
                type="submit"
                disabled={submittingReset || turnstileBlockingSubmit}
                className="w-full py-2.5 text-sm font-semibold bg-decisiv-blue text-white rounded-lg
                  hover:bg-decisiv-blue/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submittingReset ? 'Sending...' : 'Send Temporary Password'}
              </button>
            </form>

          ) : mode === 'reset-password' ? (
            /* Reset Password Form (after receiving temp password) */
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  disabled
                  className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600
                    rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Temporary Password
                </label>
                <input
                  type="text"
                  value={tempPassword}
                  onChange={e => setTempPassword(e.target.value)}
                  placeholder="Enter the code from your email"
                  required
                  autoComplete="one-time-code"
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600
                    rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                    placeholder-gray-400 dark:placeholder-gray-500 font-mono tracking-wider
                    focus:outline-hidden focus:ring-2 focus:ring-decisiv-blue focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  New Password
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  required
                  minLength={6}
                  autoComplete="new-password"
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600
                    rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                    placeholder-gray-400 dark:placeholder-gray-500
                    focus:outline-hidden focus:ring-2 focus:ring-decisiv-blue focus:border-transparent"
                />
              </div>
              <button
                type="submit"
                disabled={submittingReset}
                className="w-full py-2.5 text-sm font-semibold bg-decisiv-blue text-white rounded-lg
                  hover:bg-decisiv-blue/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submittingReset ? 'Resetting...' : 'Set New Password & Sign In'}
              </button>
            </form>

          ) : (
            /* Login / Register Form */
            <form onSubmit={handleSubmit} className="space-y-4">
              {mode === 'register' && (
                <div>
                  <label htmlFor="login-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Name (optional)
                  </label>
                  <input
                    id="login-name"
                    type="text"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="Your name"
                    autoComplete="name"
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600
                      rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                      placeholder-gray-400 dark:placeholder-gray-500
                      focus:outline-hidden focus:ring-2 focus:ring-decisiv-blue focus:border-transparent"
                  />
                </div>
              )}

              <div>
                <label htmlFor="login-email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {mode === 'login' ? 'Username or email' : 'Email'}
                </label>
                <input
                  id="login-email"
                  type={mode === 'login' ? 'text' : 'email'}
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder={mode === 'login' ? 'yourname or you@dealership.com' : 'you@dealership.com'}
                  required
                  autoComplete={mode === 'login' ? 'username' : 'email'}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600
                    rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                    placeholder-gray-400 dark:placeholder-gray-500
                    focus:outline-hidden focus:ring-2 focus:ring-decisiv-blue focus:border-transparent"
                />
              </div>

              {mode === 'register' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Company
                    </label>
                    <input
                      type="text"
                      value={company}
                      onChange={e => setCompany(e.target.value)}
                      placeholder="Your dealership or company name"
                      required
                      autoComplete="organization"
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600
                        rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                        placeholder-gray-400 dark:placeholder-gray-500
                        focus:outline-hidden focus:ring-2 focus:ring-decisiv-blue focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Why are you signing up?
                    </label>
                    <textarea
                      value={businessPurpose}
                      onChange={e => setBusinessPurpose(e.target.value)}
                      placeholder="Briefly: what do you plan to use Decisiv Performance for?"
                      required
                      rows={3}
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600
                        rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                        placeholder-gray-400 dark:placeholder-gray-500
                        focus:outline-hidden focus:ring-2 focus:ring-decisiv-blue focus:border-transparent
                        resize-none"
                    />
                  </div>
                </>
              )}

              <div>
                <label htmlFor="login-password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Password
                </label>
                <input
                  id="login-password"
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600
                    rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                    placeholder-gray-400 dark:placeholder-gray-500
                    focus:outline-hidden focus:ring-2 focus:ring-decisiv-blue focus:border-transparent"
                />
              </div>

              {/* Phase 2 bot check — register only. Login uses password+rate-limit. */}
              {mode === 'register' && honeypotInput}
              {mode === 'register' && turnstileWidget}

              <button
                type="submit"
                disabled={loading || (mode === 'register' && turnstileBlockingSubmit)}
                className="w-full py-2.5 text-sm font-semibold bg-decisiv-blue text-white rounded-lg
                  hover:bg-decisiv-blue/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading
                  ? (mode === 'login' ? 'Signing in...' : 'Creating account...')
                  : (mode === 'login' ? 'Sign in' : 'Create account')}
              </button>

              {mode === 'login' && ssoEnabled && (
                <>
                  <div className="flex items-center gap-3 text-xs text-gray-400 dark:text-gray-500">
                    <span className="flex-1 border-t border-gray-200 dark:border-gray-700" />
                    or
                    <span className="flex-1 border-t border-gray-200 dark:border-gray-700" />
                  </div>
                  <button
                    type="button"
                    onClick={() => window.location.assign('/api/auth/oidc/start')}
                    className="w-full py-2.5 text-sm font-semibold border border-decisiv-blue text-decisiv-blue
                      rounded-lg hover:bg-decisiv-blue/5 dark:hover:bg-decisiv-blue/10 transition-colors"
                  >
                    Sign in with a passkey or authenticator app
                  </button>
                </>
              )}
            </form>
          )}
        </div>

        {mode === 'login' && (
          <p className="text-center text-sm mt-4 space-x-4">
            <button
              onClick={() => { setMode('forgot-password'); setError(null); setSuccess(null); setPassword(''); }}
              className="text-decisiv-blue hover:underline font-medium"
            >
              Forgot your password?
            </button>
            <button
              onClick={handleForgotUsername}
              disabled={submittingForgotUsername}
              className="text-decisiv-blue hover:underline font-medium disabled:opacity-50"
            >
              {submittingForgotUsername ? 'Sending…' : 'Forgot your username?'}
            </button>
          </p>
        )}

        {/* Toggle login / register — only show on login/register modes */}
        {(mode === 'login' || mode === 'register') && registrationEnabled && (
          <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-6">
            {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
            <button
              onClick={() => { setMode(mode === 'login' ? 'register' : 'login'); setError(null); setSuccess(null); }}
              className="text-decisiv-blue hover:underline font-medium"
            >
              {mode === 'login' ? 'Create one' : 'Sign in'}
            </button>
          </p>
        )}

        {/* Back to sign in from forgot-password / reset-password */}
        {(mode === 'forgot-password' || mode === 'reset-password') && (
          <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-6">
            <button
              onClick={() => { setMode('login'); setError(null); setSuccess(null); setPassword(''); setTempPassword(''); setNewPassword(''); }}
              className="text-decisiv-blue hover:underline font-medium"
            >
              Back to Sign in
            </button>
          </p>
        )}
        {/* check-email and verify-email have their own inline back buttons */}

        {/* Copyright footer — version/SHA shown in sidebar after login */}
        <div className="mt-8 text-center text-[10px] text-gray-400 dark:text-gray-500">
          &copy; {new Date().getFullYear()} Decisiv, Inc. All rights reserved.
        </div>
      </div>
    </div>
  );
}
